import React, { useCallback } from 'react';

import { IconButton } from '@mui/material';
import { Copy } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

import { ChatMessageStaticRenderer } from './ChatEditor';
import type { ChatUserMessage } from '../chat-types';

const useStyles = makeStyles()(theme => ({
  actionButton: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.common.black,
    },
    // Muted by default; icons draw with currentColor so they follow this and darken on hover.
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.75),
  },
  actionsContainer: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    display: 'flex',
    gap: theme.spacing(0),
    marginTop: theme.spacing(1),
  },
  messageContent: {
    alignItems: 'flex-start',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
  userMessage: {
    '& table': {
      border: `1px solid ${theme.palette.divider}`,
      borderCollapse: 'collapse',
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(1),
      width: '100%',
    },
    '& td, & th': {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1),
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: theme.palette.grey[50],
      fontWeight: 'bold',
    },
    backgroundColor: theme.palette.grey[100],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

export const ChatPanelUserMessage = React.memo(function ChatPanelUserMessage({
  message,
}: {
  message: ChatUserMessage;
}) {
  const { classes } = useStyles();
  const dispatch = useNotificationDispatch();

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(message.text);
      dispatch({
        payload: {
          key: uuid4(),
          message: 'Prompt copied to clipboard.',
          variant: 'success',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  }, [dispatch, message.text]);

  return (
    <div className={classes.wrapper} id={message.id}>
      <div className={classes.userMessage}>
        <div className={classes.messageContent}>
          <ChatMessageStaticRenderer markdown={message.text} />
        </div>
        <div className={classes.actionsContainer}>
          <AlineTooltip arrow placement="top" title="Copy message">
            <IconButton className={classes.actionButton} size="small" onClick={handleCopy}>
              <Copy size={16} stroke="currentColor" strokeWidth={1.5} />
            </IconButton>
          </AlineTooltip>
        </div>
      </div>
    </div>
  );
});
